/**
 * Per-series "anatomic up" sign in list-index space — derived from per-slice
 * IPP (DICOM Image Position Patient).
 *
 *  +1 → increasing list-index moves anatomically up
 *  -1 → decreasing list-index moves anatomically up
 *   0 → insufficient/ambiguous data; caller falls back to the default mapping
 *
 * "Up" (top of scrollbar / wheel-up / ↑) per plane — radiologist-confirmed
 * scroll direction (DICOM LPS: +X=left, +Y=posterior, +Z=superior):
 *   Axial    → +Z patient (cranial / toward head)
 *   Coronal  → +Y patient (posterior / toward back)
 *   Sagittal → +X patient (patient's left)
 *
 * Routed by all three scroll inputs (mouse wheel, scrollbar drag, ↑/↓) so a
 * single source of truth — the actual DICOM coords of slice 0 vs slice N-1 —
 * decides which direction "up" maps to, regardless of imageIds sort order or
 * camera viewPlaneNormal sign.
 */

import type { Plane } from "./seriesClassification";

export type Vec3 = readonly [number, number, number];

// Radiologist-confirmed scroll-up direction per plane. Coronal/sagittal were
// flipped from the original DICOM-LPS "anatomic-up" (anterior / patient-right)
// to match what radiologists expect at the workstation: coronal scroll-up =
// posterior, sagittal scroll-up = patient's left. Axial (cranial) unchanged.
const PATIENT_UP_AXIS: Record<Plane, { axis: 0 | 1 | 2; sign: 1 | -1 }> = {
  Axial: { axis: 2, sign: 1 }, // +Z = cranial
  Coronal: { axis: 1, sign: 1 }, // +Y = posterior
  Sagittal: { axis: 0, sign: 1 }, // +X = patient's left
};

export function computeListAnatomicSign(
  ippFirst: Vec3 | null | undefined,
  ippLast: Vec3 | null | undefined,
  plane: Plane,
): -1 | 0 | 1 {
  if (!ippFirst || !ippLast) return 0;
  if (ippFirst.length !== 3 || ippLast.length !== 3) return 0;
  const { axis, sign: upSign } = PATIENT_UP_AXIS[plane];
  const diff = ippLast[axis] - ippFirst[axis];
  if (!Number.isFinite(diff) || Math.abs(diff) < 1e-6) return 0;
  return diff * upSign > 0 ? 1 : -1;
}
